import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import {
  DEFAULT_PLATFORM_FEE_INFO,
  resolvePlatformFeeInfo,
  type PlatformFeeInfo,
} from './platformFee';

/**
 * Fetch the server-authoritative platform fee (percent, per-user waiver,
 * waiver label) from GET /api/stripe/platform-fee. Returns display defaults
 * until the fetch resolves; refetches when sign-in state changes so the
 * per-user waiver is picked up.
 */
export function usePlatformFee(): PlatformFeeInfo {
  const { getToken, isSignedIn } = useAuth();
  const [info, setInfo] = useState<PlatformFeeInfo>(DEFAULT_PLATFORM_FEE_INFO);

  useEffect(() => {
    let cancelled = false;

    const fetchPlatformFeeInfo = async () => {
      try {
        const token = await getToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/stripe/platform-fee', { headers });
        if (!response.ok) return;

        const data = await response.json();
        if (!cancelled) {
          setInfo(resolvePlatformFeeInfo(data));
        }
      } catch (error) {
        console.error('Error fetching platform fee info:', error);
      }
    };

    fetchPlatformFeeInfo();

    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn]);

  return info;
}
